/// <amd-module name="Test/TestResultWorkItems/Source/WorkItemsGridViewSource" />
import { ITestManagementService } from 'Test/Client/TestManagementServices';
import { Service, TestCaseResult, TestToWorkItemLinks, WorkItemReference } from 'Test/common-util/Contracts/Test';
import { IWorkItemWithColorsAndIcon } from 'Test/TestResultWorkItems/Actions/WorkItemsActionHub';
import { getTFSPageData } from 'Tfs/Platform/Page';
import { IVssPageContext } from 'VSS/Platform/Context';
import { decodeUri } from 'VSS/Platform/Util/Linking';
import { WorkItem, WorkItemExpand } from 'Wit/Clients/Wit/Contracts/WorkItemTracking';
import { IWorkItemTrackingRestClient } from 'Wit/Clients/Wit/RestClient/WorkItemTracking.Types';
import * as Utils_Array from 'Wit/Common/Utilities/Array';

const ArtifactLink = "ArtifactLink";

export class WorkItemsGridViewSource {

    public loadAssociatedBugs(testCaseResult: TestCaseResult, pageContext: IVssPageContext): Promise<WorkItem[]> {

        let bugIds: number[] = [];
        let uniqueBugIds: number[] = [];
        let workItem: WorkItem[] = [];
        let tcmService = pageContext.getService<ITestManagementService>("ITestManagementService");

        let testCaseId: number = testCaseResult.testCase.id ? parseInt(testCaseResult.testCase.id) : 0;

        let getAssociatedRecentBugs = tcmService.getRecentBugs(testCaseResult.automatedTestName, testCaseId);
        let getAssociatedResultBugs = tcmService.getAssociatedBugs(parseInt(testCaseResult.testRun.id), testCaseResult.id);
        let projectContext = getTFSPageData(pageContext);
        let projectId = "";
        if (projectContext && projectContext.project) {
            projectId = projectContext.project.id;
        }

        return new Promise<WorkItem[]>(async (resolve, reject) => {
            const results = await Promise.all(([getAssociatedRecentBugs, getAssociatedResultBugs]));
            results.forEach((p) => {
                p.forEach((bug) => {
                    bugIds.push(parseInt(bug.id));
                });
            });

            uniqueBugIds = Utils_Array.unique(bugIds).reverse();
            if (uniqueBugIds.length > 0) {
                this.getWorkItemTrackingClient(pageContext).getWorkItems(uniqueBugIds, projectId, undefined, undefined, WorkItemExpand.Relations, undefined).then((workItems: WorkItem[]) => {
                    resolve(workItems);
                },
                    (error: Error) => {
                        console.log("Unable to load bugs");
                        console.log(error);
                        reject(error);
                    });
            } else {
                resolve(workItem);
            }
        });
    }

    public getUpdatedBugs(workItem: WorkItem, pageContext: IVssPageContext): Promise<WorkItem> {
        return new Promise<WorkItem>((resolve, reject) => {
            this.getWorkItemTrackingClient(pageContext).getWorkItem(workItem.id, undefined, undefined, undefined, WorkItemExpand.Relations).then((updatedWorkItem: WorkItem) => {
                resolve(updatedWorkItem);
            }, (error: Error) => {
                console.log(error);
                reject(null);
            });
        });
    }

    public deleteAssociationsOfBugs(testCaseResult: TestCaseResult, bugs: IWorkItemWithColorsAndIcon[], pageContext: IVssPageContext): Promise<WorkItem[]> {
        let count = 0;
        let deletedBugs: WorkItem[] = [];
        return new Promise<WorkItem[]>((resolve, reject) => {
            try {
                bugs.forEach((bug, index) => {
                    let data = [];
                    let relationIndicesToRemove: number[] = this._getRelationsIndicesToDelete(testCaseResult, bug.workItem);
                    for (let i = 0; i < relationIndicesToRemove.length; ++i) {
                        data.push({
                            op: "remove",
                            path: "/relations/" + relationIndicesToRemove[i].toString()
                        });
                    }

                    this.getWorkItemTrackingClient(pageContext).updateWorkItem(data, bug.workItem.id).then((workItems: WorkItem) => {
                        deletedBugs.push(bug.workItem);
                        count++;
                        if (count === bugs.length) {
                            resolve(deletedBugs);
                        }
                    }, (error: Error) => {
                        count++;
                        if (count === bugs.length) {
                            resolve(deletedBugs);
                        }
                        console.log(error);
                    });
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }

    private _getRelationsIndicesToDelete(testCaseResult: TestCaseResult, bug: WorkItem): number[] {
        let indicesToDelete = [];
        let uniqueId: string = testCaseResult.testRun.id + "." + testCaseResult.id.toString();
        /* We will delete the links to:
           1. TestResult (url of type: ../TcmResult/{RunId}.{ResultId})
           1. TestCaseRef (url of type: ../TcmTest/{TestCaseRefId})
        */
        for (let i = 0; i < bug.relations.length; ++i) {
            if (bug.relations[i].rel === ArtifactLink) {
                let decodedRelationUri = decodeUri(bug.relations[i].url);
                if ((decodedRelationUri.type === "TcmResult" && decodedRelationUri.id === uniqueId)
                    || decodedRelationUri.type === "TcmTest" && this._isMatch(decodedRelationUri.id, testCaseResult.testCaseReferenceId)) {
                    indicesToDelete.push(i);
                }
            }
        }
        return indicesToDelete;
    }

    private _isMatch(toolSpecificId: string, testCaseReferenceId: number): boolean {
        return this._getEncodedRefId(Service.Tcm, testCaseReferenceId) === toolSpecificId ||
            this._getEncodedRefId(Service.Tfs, testCaseReferenceId) === toolSpecificId;
    }

    private getWorkItemTrackingClient(pageContext: IVssPageContext) {
        return pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
    }

    private _getEncodedRefId(service: Service, testCaseRefId: number): string {
        let URI_PREFIX = "tcm";
        let URI_SEPARATOR = ".";
        return service === Service.Tcm ?
            (URI_PREFIX + URI_SEPARATOR + testCaseRefId.toString()) :
            testCaseRefId.toString();
    }

    // Source methods for Requirement
    public loadAssociatedRequirements(testCaseResult: TestCaseResult, pageContext: IVssPageContext): Promise<WorkItem[]> {

        let requirementIds: number[] = [];
        let workItem: WorkItem[] = [];
        let tcmService = pageContext.getService<ITestManagementService>("ITestManagementService");
        let projectContext = getTFSPageData(pageContext);
        let projectId = "";
        if (projectContext && projectContext.project) {
            projectId = projectContext.project.id;
        }

        return new Promise<WorkItem[]>(async (resolve, reject) => {
            try {
                if (testCaseResult.automatedTestName) {
                    let linkedRequirements: TestToWorkItemLinks = await tcmService.getTestMethodLinkedWorkItems(testCaseResult.automatedTestName);
                    let requirements: WorkItemReference[] = linkedRequirements.workItems;
                    if (requirements != null && requirements.length > 0) {
                        requirements.forEach((requirement) => {
                            requirementIds.push(parseInt(requirement.id));
                        });
                        let workItems: WorkItem[] = await this.getWorkItemTrackingClient(pageContext).getWorkItems(requirementIds, projectId, undefined, undefined, WorkItemExpand.Relations, undefined)
                        resolve(workItems);
                    } else {
                        resolve(workItem);
                    }
                }
                else {
                    resolve(workItem);
                }
            }
            catch (error) {
                reject(error);
            }
        });
    }

    public getUpdatedRequirements(workItem: WorkItem, pageContext: IVssPageContext): Promise<WorkItem> {
        return new Promise<WorkItem>((resolve, reject) => {
            this.getWorkItemTrackingClient(pageContext).getWorkItem(workItem.id).then((updatedWorkItem: WorkItem) => {
                resolve(updatedWorkItem);
            }, (error: Error) => {
                console.log(error);
                reject(null);
            });
        });
    }

    public deleteAssociationsOfRequirements(testCaseResult: TestCaseResult, requirements: IWorkItemWithColorsAndIcon[], pageContext: IVssPageContext): Promise<WorkItem[]> {
        let count = 0;
        let deletedRequirements: WorkItem[] = [];
        let tcmService = pageContext.getService<ITestManagementService>("ITestManagementService");
        return new Promise<WorkItem[]>((resolve, reject) => {
            try {
                requirements.forEach((requirement, index) => {
                    return tcmService.deleteTestMethodToWorkItemLink(testCaseResult.automatedTestName, requirement.workItem.id).then((associationDeleted: boolean) => {
                        if (associationDeleted) {
                            deletedRequirements.push(requirement.workItem);
                        }
                        count++;
                        if (count === requirements.length) {
                            resolve(deletedRequirements);
                        }
                    }, (error: Error) => {
                        count++;
                        if (count === requirements.length) {
                            resolve(deletedRequirements);
                        }
                        console.log(error);
                    });
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }
}
